import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/adminService';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';

import FailedCheckoutsPanel from './components/FailedCheckoutsPanel';
import StuckProcessingPanel from './components/StuckProcessingPanel';
import WebhookEventsPanel from './components/WebhookEventsPanel';
import ErrorAnalyticsPanel from './components/ErrorAnalyticsPanel';

const PaymentMonitoringDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30 seconds
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/agent-dashboard' },
    { label: 'Admin', path: '/admin-dashboard' },
    { label: 'Payment Monitoring', path: '/payment-monitoring-dashboard' }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'failed-checkouts', label: 'Failed Checkouts', icon: 'XCircle' },
    { id: 'stuck-processing', label: 'Stuck Processing', icon: 'Clock' },
    { id: 'webhook-events', label: 'Webhook Events', icon: 'Webhook' },
    { id: 'error-analytics', label: 'Error Analytics', icon: 'BarChart3' }
  ];

  useEffect(() => {
    checkAdminAccess();
  }, [user]);

  useEffect(() => {
    let intervalId;
    if (autoRefresh && isAdmin) {
      intervalId = setInterval(() => {
        handleRefresh();
      }, refreshInterval);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoRefresh, refreshInterval, isAdmin]);

  const checkAdminAccess = async () => {
    setLoading(true);
    try {
      const adminStatus = await adminService?.isAdmin();
      setIsAdmin(adminStatus);
      
      if (!adminStatus) {
        navigate('/agent-dashboard');
      }
    } catch (error) {
      console.error('Admin check error:', error);
      navigate('/agent-dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setLastRefresh(new Date());
  };

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Payment Monitoring Dashboard - OfferSense</title>
        </Helmet>
        <Header />
        <main className="container mx-auto px-4 py-6 md:py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="h-32 bg-muted rounded"></div>
              <div className="h-32 bg-muted rounded"></div>
              <div className="h-32 bg-muted rounded"></div>
              <div className="h-32 bg-muted rounded"></div>
            </div>
            <div className="h-96 bg-muted rounded"></div>
          </div>
        </main>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Payment Monitoring Dashboard - OfferSense</title>
        <meta name="description" content="Real-time tracking of payment processing issues and Stripe webhook events for immediate troubleshooting" />
      </Helmet>

      <Header />

      <main className="container mx-auto px-4 py-6 md:py-8">
        <Breadcrumbs items={breadcrumbItems} />

        <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
              Payment Monitoring Dashboard
            </h1>
            <p className="text-muted-foreground text-sm md:text-base">
              Real-time tracking of failed checkouts, stuck processing states, and Stripe webhook events
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleAutoRefresh}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                autoRefresh
                  ? 'bg-success text-success-foreground hover:bg-success/90'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <Icon name={autoRefresh ? 'Pause' : 'Play'} size={16} />
              {autoRefresh ? 'Auto-Refresh On' : 'Auto-Refresh Off'}
            </button>
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Icon name="RefreshCw" size={16} />
              Refresh
            </button>
          </div>
        </div>

        {autoRefresh && (
          <div className="mb-4 flex items-center gap-3 text-sm text-muted-foreground">
            <Icon name="Clock" size={14} />
            <span>Auto-refreshing every {refreshInterval / 1000}s</span>
            <span>•</span>
            <span>Last refresh: {lastRefresh?.toLocaleTimeString()}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-border mb-6">
          <div className="flex gap-1 overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 gap-6">
              <FailedCheckoutsPanel compact limit={5} refreshTrigger={lastRefresh} />
              <StuckProcessingPanel compact limit={5} refreshTrigger={lastRefresh} />
              <WebhookEventsPanel compact limit={5} refreshTrigger={lastRefresh} />
              <ErrorAnalyticsPanel compact refreshTrigger={lastRefresh} />
            </div>
          )}

          {activeTab === 'failed-checkouts' && <FailedCheckoutsPanel refreshTrigger={lastRefresh} />}

          {activeTab === 'stuck-processing' && <StuckProcessingPanel refreshTrigger={lastRefresh} />}

          {activeTab === 'webhook-events' && <WebhookEventsPanel refreshTrigger={lastRefresh} />}

          {activeTab === 'error-analytics' && <ErrorAnalyticsPanel refreshTrigger={lastRefresh} />}
        </div>
      </main>
    </div>
  );
};

export default PaymentMonitoringDashboard;